import { z } from 'zod';
import { ValidationPatterns } from './shared';
const patterns = {
  userId: z.string().min(1),
  nonNegativeInteger: z.number().int().min(0),
  nonNegativeNumber: z.number().min(0),
  hour: z.number().int().min(0).max(23),
  dayName: z.string().max(20),
};
const BaseUserStatsSchema = z.object({
  userId: ValidationPatterns.flexibleObjectId,
  totalWordsLearned: patterns.nonNegativeInteger,
  totalInteractions: patterns.nonNegativeInteger,
  sessionCount: patterns.nonNegativeInteger,
  currentStreak: patterns.nonNegativeInteger,
  longestStreak: patterns.nonNegativeInteger,
  wordsThisWeek: patterns.nonNegativeInteger,
  wordsThisMonth: patterns.nonNegativeInteger,
  averageWordsPerDay: patterns.nonNegativeNumber,
  averageInteractionsPerWord: patterns.nonNegativeNumber,
  averageSessionLength: z.number(),
  mostActiveDay: patterns.dayName,
  mostActiveHour: patterns.hour,
  lastActivityAt: ValidationPatterns.flexibleDate,
});
export const UserStatsSchemas = {
  full: BaseUserStatsSchema,
  dashboard: BaseUserStatsSchema.pick({
    userId: true,
    totalWordsLearned: true,
    totalInteractions: true,
    currentStreak: true,
    longestStreak: true,
    wordsThisWeek: true,
    wordsThisMonth: true,
    averageWordsPerDay: true,
    averageInteractionsPerWord: true,
    mostActiveDay: true,
    mostActiveHour: true,
    lastActivityAt: true,
    sessionCount: true,
    averageSessionLength: true,
  }),
  overview: BaseUserStatsSchema.pick({
    totalWordsLearned: true,
    currentStreak: true,
    longestStreak: true,
    wordsThisWeek: true,
    wordsThisMonth: true,
    averageWordsPerDay: true,
  }),
} as const;
export const InteractionType = {
  WORD_LEARNED: 'word_learned',
  AUDIO_PLAYED: 'audio_played',
  WORD_VIEWED: 'word_viewed',
  SESSION_COMPLETED: 'session_completed',
  SESSION_STARTED: 'session_started',
  EXAMPLE_VIEWED: 'example_viewed',
  CAROUSEL_NAVIGATED: 'carousel_navigated',
  PREFERENCES_UPDATED: 'preferences_updated',
} as const;
export type InteractionTypeValue =
  (typeof InteractionType)[keyof typeof InteractionType];
export const InteractionSource = {
  Telegram: 'telegram',
  Webview: 'webview',
  WhatsApp: 'whatsapp',
  Bot: 'bot',
  BOT: 'bot', // Backward compatibility
  WEB: 'webview', // Backward compatibility
} as const;
export type InteractionSourceValue =
  (typeof InteractionSource)[keyof typeof InteractionSource];
const BaseUserInteractionSchema = z.object({
  userId: ValidationPatterns.flexibleObjectId,
  wordId: ValidationPatterns.flexibleObjectId,
  interactionId: z.string().optional(),
  collectionId: ValidationPatterns.flexibleObjectId.optional(),
  interactionType: z.enum(InteractionType),
  timestamp: ValidationPatterns.flexibleDate,
  source: z.enum(InteractionSource).optional(),
  sessionId: z.string().optional(),
  difficulty: z.number().int().min(1).max(5).optional(),
  metadata: z.record(z.string(), z.any()).optional(),
  createdAt: ValidationPatterns.flexibleDate.optional(),
  updatedAt: ValidationPatterns.flexibleDate.optional(),
});
export const UserInteractionSchemas = {
  base: BaseUserInteractionSchema,
  enriched: BaseUserInteractionSchema.extend({
    word: z.string().optional(),
    example: z.string().optional(),
  }),
} as const;
const BaseTopicProgressSchema = z.object({
  userId: ValidationPatterns.flexibleObjectId,
  topic: z.string().min(1).max(100),
  totalWords: patterns.nonNegativeInteger,
  learnedWords: patterns.nonNegativeInteger,
  completionPercentage: z.number().min(0).max(100),
  lastLearned: ValidationPatterns.flexibleDate.optional(),
  streak: patterns.nonNegativeInteger,
  averageRetention: z.number().min(0).max(1).optional(),
  difficultyRating: z.number().min(1).max(5).optional(),
  createdAt: ValidationPatterns.flexibleDate,
  updatedAt: ValidationPatterns.flexibleDate,
});
export const TopicProgressSchemas = {
  full: BaseTopicProgressSchema,
  display: BaseTopicProgressSchema.pick({
    topic: true,
    totalWords: true,
    learnedWords: true,
    completionPercentage: true,
    lastLearned: true,
    streak: true,
  }).extend({
    percent: z.number().min(0).max(100),
  }),
} as const;
export const StatsAPI = {
  dashboard: {
    request: z.object({
      interactionsLimit: z
        .number()
        .optional()
        .default(5)
        .pipe(z.number().int().min(1).max(100)),
    }),
    response: z.object({
      stats: UserStatsSchemas.dashboard,
      recentInteractions: z.array(UserInteractionSchemas.base),
      progress: z.array(TopicProgressSchemas.display),
    }),
  },
  overview: {
    request: z.object({}),
    response: UserStatsSchemas.overview,
  },
  interactions: {
    request: z.object({
      limit: z.number().optional().pipe(z.number().int().min(1).max(1000)),
      journeyOnly: z.boolean().optional(),
    }),
    response: z.object({
      interactions: z.array(UserInteractionSchemas.base),
    }),
  },
  progress: {
    request: z.object({}),
    response: z.object({
      progress: z.array(TopicProgressSchemas.display),
    }),
  },
} as const;
export type UserStats = z.infer<typeof UserStatsSchemas.full>;
export type DashboardStats = z.infer<typeof UserStatsSchemas.dashboard>;
export type OverviewStats = z.infer<typeof UserStatsSchemas.overview>;
export type UserInteraction = z.infer<typeof UserInteractionSchemas.base>;
export type TopicProgress = z.infer<typeof TopicProgressSchemas.full>;
export type TopicProgressDisplay = z.infer<typeof TopicProgressSchemas.display>;
export type DashboardStatsRequest = z.infer<typeof StatsAPI.dashboard.request>;
export type DashboardStatsResponse = z.infer<
  typeof StatsAPI.dashboard.response
>;
export type OverviewStatsRequest = z.infer<typeof StatsAPI.overview.request>;
export type OverviewStatsResponse = z.infer<typeof StatsAPI.overview.response>;
export type InteractionsRequest = z.infer<typeof StatsAPI.interactions.request>;
export type InteractionsResponse = z.infer<
  typeof StatsAPI.interactions.response
>;
export type ProgressRequest = z.infer<typeof StatsAPI.progress.request>;
export type ProgressResponse = z.infer<typeof StatsAPI.progress.response>;
